import { useCallback, useEffect, useRef, useState, MouseEvent } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { GetShip } from "../App/Context"
import { useEditState } from "../App/useEditState"
import { useRefCallbacks } from "../App/useRefCallbacks"
import { useSubscription } from "../App/useSubscription"
import { EditGroupData } from "../Components/EditGroup"
import { Icons } from "../Components/Icon"
import { Overlay, OverlayTitle } from "../Components/Overlay"
import { SelectOption } from "../Components/Select"
import { TabBar, TabBarCallbacks } from "../Components/TabBar"
import { useOverlayScroll } from "../Components/useOverlayScroll"
import { OverlayScrollFooter } from "../Components/OverlayScrollFooter"
import { ILogbookAppNMEAStatusResult, ILogbookAppStatusResult, INavigationThresholds, ISettingsType } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { GetShipView } from "./ShipView"
import { logger, fieldNames } from "../App/Logger"
import { NMEAConfigurationConnectionView } from "./NMEAConfiguration/ConnectionView"
import { NMEAConfigurationNativeView } from "./NMEAConfiguration/NativeView"
import { NMEAConfigurationTestDataView } from "./NMEAConfiguration/TestDataView"
import { NMEAConfigurationConditionsView } from "./NMEAConfiguration/ConditionsView"
import { NMEAConfigurationContext, NMEAConfigurationContextData, NMEAConfigurationViewData, NMEAStatusChecks } from "./NMEAConfiguration/Context"
import { NMEAConfigurationViewProps } from "./NMEAConfiguration/Params"
import { latestParsedSettings, useSettingsWriters } from "../Data"

export function NMEAConfigurationView({ banner, initConfiguration, initConnectionTypeOpen, initTab }: NMEAConfigurationViewProps) {
  const app = GetApp()!;
  const t = app.getT("NMEAConfigurationView");
  const ship = GetShip();
  const settingsWriters = useSettingsWriters(ship.id);
  const { resetShipOverlay } = GetShipView();
  const [connecting, setConnecting] = useState<boolean>(false);
  // "The user asked this view to connect and that request has not been honoured yet."
  // Deliberately separate from `connecting` (which is UI state for the Connecting…
  // label and must clear on a failed attempt) -- see the subscription handler below.
  const awaitingConnection = useRef<boolean>(false);
  const [connectionType, setConnectionType] = useState<string>("tcp");
  const [courseChecked, setCourseChecked] = useState<boolean>(false);
  const [engineIdleChecked, setEngineIdleChecked] = useState<boolean>(false);
  const [startStopAnchorChecked, setStartStopAnchorChecked] = useState<boolean>(false);
  const [nativePressureChecked, setNativePressureChecked] = useState<boolean>(false);
  const [device, setDevice] = useState<string | undefined>(undefined);
  const [devices, setDevices] = useState<string[]>([]);
  const [edited, onEdited, resetEdited] = useEditState();
  const [error, setError] = useState<string | undefined>(app.status?.nmea?.error);
  const [host, setHost] = useState<string | undefined>(undefined);
  const tabBarRef = useRefCallbacks<TabBarCallbacks>();
  const [port, setPort] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<ILogbookAppNMEAStatusResult | undefined>(app.status?.nmea);
  const [thresholds, setThresholds] = useState<INavigationThresholds | undefined>(undefined);
  const [interval, setInterval] = useState<number | undefined>(undefined);
  const completedCallback = useCallback(() => resetShipOverlay(), []);
  const { bodyRef, showScrollButton, isAtBottom, handleScrollMore } = useOverlayScroll();

  const connectionTypes = [
    {
      icon: Icons.Wifi,
      title: t("connection_type_tcp"),
      value: "tcp",
    },
    app.hasFeature("usb") ? {
      icon: Icons.Usb,
      title: t("connection_type_usb"),
      value: "usb",
    } : undefined,
    app.hasFeature("gps") ? {
      icon: Icons.Gps,
      title: t("connection_type_gps"),
      value: "gps",
    } : undefined,
    {
      icon: Icons.Wifi,
      title: t("connection_type_udp"),
      value: "udp",
    },
    // eslint-disable-next-line no-constant-condition -- Bluetooth option intentionally disabled, kept for future re-enablement
    false ? {
      icon: Icons.Bluetooth,
      title: t("connection_type_bluetooth"),
      value: "bluetooth",
    } : undefined,
  ].filter(Boolean) as SelectOption[];
  const intervalOptions = [
    {
      title: "60 min",
      value: 60 * 60,
    },
    {
      title: "30 min",
      value: 30 * 60,
    },
  ];
  useEffect(() => {
    const load = async () => {
      if (initConfiguration) {
        setConnectionType(initConfiguration.type || "tcp");
        setDevice(initConfiguration.device);
        setHost(initConfiguration.host);
        setPort(initConfiguration.port);
        setDevices(initConfiguration.devices || []);
        return;
      }
      if (edited) {
        return;
      }
      const data = await NMEAConfigurationViewData.loadNmea(app.apiClient);
      if (data.configuration) {
        setConnectionType(data.configuration.type || "tcp");
        setDevice(data.configuration.device);
        setHost(data.configuration.host);
        setPort(data.configuration.port);
      }
      setNativePressureChecked(data.nativeBaro || false);
      setDevices(data.devices || []);
    };
    load().catch(console.error);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!ship) {
        return;
      }
      const settings = await Ship.shipSettings(ship);
      if (settings.thresholds) {
        setThresholds(settings.thresholds);
        setCourseChecked(settings.thresholds.courseChangeEnabled);
        setEngineIdleChecked(settings.thresholds.engineIdleRpmEnabled);
        setStartStopAnchorChecked(settings.thresholds.startStopAnchor);
        setInterval(settings.thresholds.interval || (60 * 60));
      } else {
        setThresholds({} as INavigationThresholds);
        setInterval(60 * 60);
      }
    };
    load().catch(console.error);
  }, [ship]);

  const toggleNmeaSetup = useCallback((e?: MouseEvent<HTMLAnchorElement>) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    completedCallback();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const connect = useCallback(async (_data: FormData, values: any) => {
    logger.debug("saving", fieldNames(values));
    awaitingConnection.current = true;
    setConnecting(true);
    setError(undefined);
    await NMEAConfigurationViewData.connectNmea(app.apiClient, values);
    let currentStatus = status;
    for (let attempt = 0; awaitingConnection.current
      && (!currentStatus?.connected || Object.keys(currentStatus.data ?? {}).length === 0)
      && attempt < 100; attempt++) {
      currentStatus = (await NMEAConfigurationViewData.loadStatus(app.apiClient)).nmea;
      if (!currentStatus?.connected || Object.keys(currentStatus.data ?? {}).length === 0) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }
    if (currentStatus?.connected && awaitingConnection.current) {
      awaitingConnection.current = false;
      setStatus(currentStatus);
      tabBarRef.current?.setSelected("test_data");
      setConnecting(false);
    } else if (currentStatus?.error) {
      setError(currentStatus.error);
      setConnecting(false);
    }
  }, [connecting, status?.connected, tabBarRef]);
  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    if (status.nmea?.data) {
      setStatus(status.nmea);
    }

    // Hand over to the test-data tab on `awaitingConnection`, NOT on `connecting`.
    // The two look interchangeable and are not: `connecting` also means "the connect
    // button is replaced by the Connecting… label" (`ConnectionView.tsx`), so it has
    // to be cleared as soon as an attempt reports an error -- while the request the
    // user made is still outstanding, because `LogbookNMEAClient` retries on its own
    // (`connectionFailed` -> `connectPending` -> `connected`, no user action).
    //
    // Clearing both on the error left the successful retry arriving with nothing set,
    // so the view sat on the connection tab with a live, streaming feed and no way to
    // reach the data short of clicking connect again. `awaitingConnection` outlives
    // the failed attempt and is cleared only once it has actually been honoured (or
    // the user resets the connection), which is what makes the retry land.
    if (status.nmea?.connected && awaitingConnection.current) {
      awaitingConnection.current = false;
      tabBarRef.current?.setSelected("test_data");
      setConnecting(false);
      setError(undefined);
    }

    if (status.nmea?.error) {
      tabBarRef.current?.setSelected("connection");
      setError(status.nmea.error);
      setConnecting(false);
    }
  }, Subscription.AppStatus);

  const selectConnectionType = useCallback((type: string) => {
    logger.debug("selectConnectionType", type);
    setConnectionType(type);
  }, [connectionType]);

  const selectIntervalOptions = useCallback((type: number) => {
    logger.debug("selectInterval", type);
    setInterval(type);
    onEdited();
  }, []);

  async function resetConnection(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    await NMEAConfigurationViewData.disconnectNmea(app.apiClient);
    // Explicitly dropping the connection also withdraws any outstanding request to be
    // taken to the test-data tab -- otherwise a reconnect the app makes later on its
    // own would still yank the user there, long after they said they didn't want it.
    awaitingConnection.current = false;
    setConnecting(false);
    setStatus(undefined);
    tabBarRef.current?.setSelected("connection");
    completedCallback();
  }

  const updateThresholds = useCallback(async (_data: FormData, values: EditGroupData) => {
    if (values.thresholds) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
      (values.thresholds as any).courseChangeEnabled = courseChecked;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
      (values.thresholds as any).engineIdleRpmEnabled = engineIdleChecked;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
      (values.thresholds as any).startStopAnchor = startStopAnchorChecked;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
    const t = values.thresholds as any;
    const validationError = NMEAConfigurationViewData.validateEngineRpm(
      t?.engineStop ?? thresholds?.engineStop,
      t?.engineStart ?? thresholds?.engineStart,
      t?.engineIdleRpm ?? thresholds?.engineIdleRpm,
      engineIdleChecked,
    );
    if (validationError) {
      setError(validationError);
      resetEdited();
      return;
    }
    setError(undefined);
    logger.debug("save", fieldNames(values));
    const result = await settingsWriters.updateSettings(values);
    const parsed = latestParsedSettings(result);
    if (parsed?.settingType === ISettingsType.ship) {
      setThresholds(parsed.dataParsed.thresholds);
    }
    resetEdited();
  }, [courseChecked, engineIdleChecked, startStopAnchorChecked, thresholds, ship, settingsWriters]);

  const setNativePressure = useCallback(async (enabled: boolean) => {
    await NMEAConfigurationViewData.setNativePressure(app.apiClient, enabled);
    setNativePressureChecked(enabled);
  }, [app]);

  if (banner) {
    return (
      <div className="NMEAConfigurationView-banner">
        {t("banner_text")} <a href="#" className="NMEAConfigurationView-banner-start-setup" onClick={toggleNmeaSetup}>{t("banner_start_setup")}</a>
      </div>
    );
  }

  const statusChecks: NMEAStatusChecks = NMEAConfigurationViewData.buildStatusChecks(status?.data, Ship.engines(ship));

  const contextValue: NMEAConfigurationContextData = {
    connecting,
    connectionType,
    connectionTypes,
    selectConnectionType,
    initConnectionTypeOpen,
    host,
    port,
    device,
    devices,
    connect,
    resetConnection,
    nativePressureChecked,
    setNativePressure,
    statusChecks,
    status,
    interval,
    intervalOptions,
    selectIntervalOptions,
    courseChecked,
    setCourseChecked,
    engineIdleChecked,
    setEngineIdleChecked,
    startStopAnchorChecked,
    setStartStopAnchorChecked,
    thresholds,
    updateThresholds,
    edited,
    onEdited,
    error,
    completedCallback,
  };

  return (
    <div className="NMEAConfigurationView">
      <Overlay closeCallback={completedCallback} className="overlay-max-height">
        <OverlayTitle closeCallback={toggleNmeaSetup} title={t("title")} />
        <div ref={bodyRef} className="Overlay-body">
          <NMEAConfigurationContext.Provider value={contextValue}>
            <TabBar defaultSelection={initTab ?? "connection"} callbacks={tabBarRef} tabs={[
              { name: "connection", title: t("step_connection"), render: () => <NMEAConfigurationConnectionView /> },
              ...(app.hasFeature("gps") ? [{
                name: "native", title: t("step_native"), render: () => <NMEAConfigurationNativeView />,
              }] : []),
              { name: "test_data", title: t("step_test_data"), render: () => <NMEAConfigurationTestDataView /> },
              { name: "conditions", title: t("step_conditions"), render: () => <NMEAConfigurationConditionsView /> },
            ]}
            />
          </NMEAConfigurationContext.Provider>
        </div>
        <OverlayScrollFooter isAtBottom={isAtBottom} showScrollButton={showScrollButton} handleScrollMore={handleScrollMore} readMoreLabel={t("read_more")} />
      </Overlay>
    </div>
  );
}

// Re-export the shared data layer so existing `from "./NMEAConfigurationView"`
// imports keep working after the split into Views/NMEAConfiguration/.
export { NMEAConfigurationViewData } from "./NMEAConfiguration/Context"
